import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ════════════════════════════════════════════════════════════════════════════
// COLORS - Light Mode
// ════════════════════════════════════════════════════════════════════════════
const COLORS = {
  celerway: '#FF6B35',
  celerwayLight: 'rgba(255, 107, 53, 0.1)',
  paloAlto: '#FA582D',
  paloAltoDark: '#C74634',
  hp: '#0096D6',
  hpLight: 'rgba(0, 150, 214, 0.1)',
  secure: '#16A34A',
  secureLight: 'rgba(22, 163, 74, 0.1)',
  blocked: '#DC2626',
  blockedLight: 'rgba(220, 38, 38, 0.1)',
  dark: '#1E293B',
  darkAlt: '#0F172A',
  slate: '#64748B',
  white: '#FFFFFF',
  lightBg: '#F8FAFC',
  cardBg: '#FFFFFF',
  border: '#E2E8F0',
};

// ════════════════════════════════════════════════════════════════════════════
// PRINTER NODE - Methodist Hospital
// ════════════════════════════════════════════════════════════════════════════
const PrinterNode = ({ data }) => (
  <div className="relative group">
    <Handle type="target" position={Position.Top} className="!bg-cyan-500 !w-3 !h-3 !border-2 !border-white" />
    
    <div className="relative bg-white rounded-2xl p-5 border border-slate-200 shadow-xl min-w-[220px]"
         style={{ boxShadow: '0 4px 20px rgba(0, 150, 214, 0.15)' }}>
      
      {/* HP Badge */}
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#0096D6] rounded-full shadow-md">
        <span className="text-[10px] font-bold text-white tracking-wider">HP ENTERPRISE</span>
      </div>
      
      {/* Status indicator */}
      <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white shadow-md animate-pulse" />
      
      {/* Printer Icon */}
      <div className="flex justify-center mb-3 mt-2">
        <div className="w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl flex items-center justify-center border border-slate-200">
          <svg className="w-10 h-10 text-slate-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
          </svg>
        </div>
      </div>
      
      {/* Printer Info */}
      <div className="text-center space-y-1">
        <div className="text-sm font-bold text-slate-800">{data.model}</div>
        <div className="text-xs text-cyan-600 font-medium">{data.location}</div>
        <div className="text-[10px] text-slate-500">{data.hospital}</div>
      </div>
      
      {/* Metrics */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <div className="bg-slate-50 rounded-lg p-2 text-center border border-slate-100">
          <div className="text-[10px] text-slate-500 uppercase">Toner</div>
          <div className="text-sm font-bold text-emerald-600">{data.toner}%</div>
        </div>
        <div className="bg-slate-50 rounded-lg p-2 text-center border border-slate-100">
          <div className="text-[10px] text-slate-500 uppercase">Uptime</div>
          <div className="text-sm font-bold text-emerald-600">{data.uptime}%</div>
        </div>
      </div>
      
      {/* Security Badge */}
      <div className="mt-3 flex items-center justify-center gap-1 py-1.5 bg-emerald-50 rounded-lg border border-emerald-200">
        <svg className="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        <span className="text-[10px] font-medium text-emerald-700">HIPAA Isolated</span>
      </div>
    </div>
    
    <Handle type="source" position={Position.Bottom} className="!bg-cyan-500 !w-3 !h-3 !border-2 !border-white" />
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// X2M GATEWAY NODE - The Star of the Show
// ════════════════════════════════════════════════════════════════════════════
const X2MGatewayNode = ({ data }) => (
  <div className="relative group">
    <Handle type="target" position={Position.Top} className="!bg-orange-500 !w-3 !h-3 !border-2 !border-white" />
    <Handle type="target" position={Position.Left} id="left" className="!bg-orange-500 !w-3 !h-3 !border-2 !border-white" />
    <Handle type="target" position={Position.Right} id="right" className="!bg-orange-500 !w-3 !h-3 !border-2 !border-white" />
    
    <div className="relative bg-white rounded-3xl border-2 border-orange-400 shadow-2xl overflow-hidden"
         style={{ 
           boxShadow: '0 8px 40px rgba(255, 107, 53, 0.2)',
           minWidth: 340,
         }}>
      
      {/* Header Bar with Celerway Branding */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-sm">C</span>
            </div>
            <div>
              <div className="text-white font-bold text-sm tracking-wide">CELERWAY X2M</div>
              <div className="text-orange-200 text-[10px]">Sovereign Edge Gateway</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            <span className="text-[10px] text-white/80">ONLINE</span>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="p-5 space-y-4">
        
        {/* VM Container - Palo Alto NGFW */}
        <div className="relative bg-gradient-to-br from-red-50 to-orange-50 rounded-xl border border-red-200 p-4">
          
          {/* Palo Alto Badge */}
          <div className="absolute -top-2 left-4 px-2 py-0.5 bg-gradient-to-r from-red-600 to-orange-600 rounded text-[9px] font-bold text-white shadow-md">
            PALO ALTO NETWORKS
          </div>
          
          <div className="mt-2 flex items-start gap-3">
            {/* NGFW Icon */}
            <div className="w-14 h-14 bg-gradient-to-br from-red-100 to-orange-100 rounded-xl flex items-center justify-center border border-red-200 flex-shrink-0">
              <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
              </svg>
            </div>
            
            <div className="flex-1">
              <div className="text-slate-800 font-semibold text-sm">Next-Gen Firewall VM</div>
              <div className="text-slate-500 text-xs mt-0.5">Prisma Access • Threat Prevention</div>
              
              {/* Security Features */}
              <div className="mt-2 flex flex-wrap gap-1">
                {['Zero Trust', 'IPS/IDS', 'SSL Decrypt', 'App-ID'].map(feature => (
                  <span key={feature} className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[8px] rounded border border-red-200 font-medium">
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          </div>
          
          {/* Threat Stats */}
          <div className="mt-3 grid grid-cols-3 gap-2">
            <div className="bg-white rounded-lg p-2 text-center border border-slate-100 shadow-sm">
              <div className="text-[9px] text-slate-500">BLOCKED</div>
              <div className="text-sm font-bold text-red-500">2.4K</div>
            </div>
            <div className="bg-white rounded-lg p-2 text-center border border-slate-100 shadow-sm">
              <div className="text-[9px] text-slate-500">INSPECTED</div>
              <div className="text-sm font-bold text-orange-500">847K</div>
            </div>
            <div className="bg-white rounded-lg p-2 text-center border border-slate-100 shadow-sm">
              <div className="text-[9px] text-slate-500">ENCRYPTED</div>
              <div className="text-sm font-bold text-emerald-500">100%</div>
            </div>
          </div>
        </div>
        
        {/* Connectivity Stack */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-cyan-100 rounded flex items-center justify-center">
                <svg className="w-4 h-4 text-cyan-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
              </div>
              <span className="text-xs font-medium text-slate-700">5G/LTE</span>
            </div>
            <div className="text-[10px] text-slate-500">Multi-carrier bonding</div>
            <div className="text-xs font-bold text-cyan-600 mt-1">↑ 85 Mbps</div>
          </div>
          
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-emerald-100 rounded flex items-center justify-center">
                <svg className="w-4 h-4 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                </svg>
              </div>
              <span className="text-xs font-medium text-slate-700">IPSec VPN</span>
            </div>
            <div className="text-[10px] text-slate-500">AES-256-GCM</div>
            <div className="text-xs font-bold text-emerald-600 mt-1">● Active</div>
          </div>
        </div>
        
        {/* Air Gap Indicator */}
        <div className="flex items-center gap-2 py-2 px-3 bg-emerald-50 rounded-lg border border-emerald-200">
          <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <div className="text-xs font-semibold text-emerald-700">Network-Isolated from Hospital LAN</div>
            <div className="text-[10px] text-emerald-600/70">PHI never touches local network</div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="px-5 py-3 bg-slate-50 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <div className="text-[10px] text-slate-500">Methodist Hospital • Austin, TX</div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400">Serial: X2M-ATX-00847</span>
          </div>
        </div>
      </div>
    </div>
    
    <Handle type="source" position={Position.Bottom} className="!bg-orange-500 !w-3 !h-3 !border-2 !border-white" />
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// PALO ALTO CLOUD NODE
// ════════════════════════════════════════════════════════════════════════════
const PaloAltoCloudNode = ({ data }) => (
  <div className="relative group">
    <Handle type="target" position={Position.Bottom} className="!bg-red-500 !w-3 !h-3 !border-2 !border-white" />
    
    <div className="relative bg-white rounded-2xl border border-red-200 shadow-xl overflow-hidden min-w-[260px]"
         style={{ boxShadow: '0 4px 20px rgba(250, 88, 45, 0.15)' }}>
      
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-orange-500 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-md">
            <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#FA582D"/>
              <path d="M2 17l10 5 10-5" stroke="#FA582D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12l10 5 10-5" stroke="#FA582D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <div className="text-white font-bold">Palo Alto Networks</div>
            <div className="text-red-200 text-xs">Prisma Access Cloud</div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Services */}
        <div className="space-y-2">
          {[
            { name: 'Threat Intelligence', status: 'Synced', icon: '🛡️' },
            { name: 'Policy Management', status: 'Active', icon: '📋' },
            { name: 'Security Analytics', status: 'Real-time', icon: '📊' },
          ].map(service => (
            <div key={service.name} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
              <div className="flex items-center gap-2">
                <span className="text-sm">{service.icon}</span>
                <span className="text-xs text-slate-700 font-medium">{service.name}</span>
              </div>
              <span className="text-[10px] text-emerald-600 font-medium">{service.status}</span>
            </div>
          ))}
        </div>
        
        {/* Cortex XDR Badge */}
        <div className="flex items-center gap-2 py-2 px-3 bg-red-50 rounded-lg border border-red-200">
          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          <span className="text-xs text-red-600 font-medium">Cortex XDR Integration</span>
        </div>
      </div>
    </div>
    
    <Handle type="source" position={Position.Bottom} id="down" className="!bg-red-500 !w-3 !h-3 !border-2 !border-white" />
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// HP PRINT CLOUD NODE
// ════════════════════════════════════════════════════════════════════════════
const HPPrintCloudNode = ({ data }) => (
  <div className="relative group">
    <Handle type="target" position={Position.Bottom} className="!bg-cyan-500 !w-3 !h-3 !border-2 !border-white" />
    
    <div className="relative bg-white rounded-2xl border border-cyan-200 shadow-xl overflow-hidden min-w-[260px]"
         style={{ boxShadow: '0 4px 20px rgba(0, 150, 214, 0.15)' }}>
      
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-500 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-md">
            <span className="text-[#0096D6] font-black text-lg">hp</span>
          </div>
          <div>
            <div className="text-white font-bold">HP Print Cloud</div>
            <div className="text-cyan-200 text-xs">Smart Device Services</div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Fleet Stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-slate-50 rounded-lg p-2 text-center border border-slate-100">
            <div className="text-[9px] text-slate-500 uppercase">Fleet Size</div>
            <div className="text-lg font-bold text-cyan-600">{data.fleetSize}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2 text-center border border-slate-100">
            <div className="text-[9px] text-slate-500 uppercase">Online</div>
            <div className="text-lg font-bold text-emerald-600">100%</div>
          </div>
        </div>
        
        {/* Services */}
        <div className="space-y-2">
          {[
            { name: 'Fleet Management', icon: '🖨️' },
            { name: 'Supply Auto-Order', icon: '📦' },
            { name: 'Remote Diagnostics', icon: '🔧' },
          ].map(service => (
            <div key={service.name} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
              <span className="text-sm">{service.icon}</span>
              <span className="text-xs text-slate-700 font-medium">{service.name}</span>
              <div className="ml-auto w-2 h-2 bg-emerald-500 rounded-full" />
            </div>
          ))}
        </div>
        
        {/* UK Healthcare Badge */}
        <div className="flex items-center gap-2 py-2 px-3 bg-cyan-50 rounded-lg border border-cyan-200">
          <span className="text-sm">🏥</span>
          <span className="text-xs text-cyan-700 font-medium">Healthcare Managed Services</span>
        </div>
      </div>
    </div>
    
    <Handle type="source" position={Position.Bottom} id="down" className="!bg-cyan-500 !w-3 !h-3 !border-2 !border-white" />
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// BLOCKED HOSPITAL LAN NODE
// ════════════════════════════════════════════════════════════════════════════
const BlockedLANNode = ({ data }) => (
  <div className="relative">
    <Handle type="target" position={Position.Left} className="!bg-red-500 !w-3 !h-3 !border-2 !border-white" />
    
    <div className="relative bg-white rounded-xl border-2 border-red-300 p-4 min-w-[180px] shadow-lg"
         style={{ boxShadow: '0 4px 20px rgba(220, 38, 38, 0.15)' }}>
      
      {/* Blocked X */}
      <div className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center border-2 border-white shadow-lg">
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center border border-red-200">
          <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
          </svg>
        </div>
        <div>
          <div className="text-sm font-semibold text-red-600">Hospital LAN</div>
          <div className="text-[10px] text-slate-500">Methodist IT Network</div>
        </div>
      </div>
      
      <div className="mt-3 text-center py-2 bg-red-100 rounded-lg border border-red-200">
        <span className="text-[10px] font-bold text-red-600">NO ACCESS • ISOLATED</span>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// NODE TYPES
// ════════════════════════════════════════════════════════════════════════════
const nodeTypes = {
  printer: PrinterNode,
  x2mGateway: X2MGatewayNode,
  paloAltoCloud: PaloAltoCloudNode,
  hpPrintCloud: HPPrintCloudNode,
  blockedLAN: BlockedLANNode,
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
const SovereignEdgeTopologyLight = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);

  const buildTopology = useCallback(() => {
    const CENTER_X = 500;
    
    const newNodes = [
      // Palo Alto Cloud (top left)
      {
        id: 'palo-cloud',
        type: 'paloAltoCloud',
        position: { x: CENTER_X - 280, y: 0 },
        data: {},
      },
      // HP Print Cloud (top right)
      {
        id: 'hp-cloud',
        type: 'hpPrintCloud',
        position: { x: CENTER_X + 120, y: 0 },
        data: { fleetSize: '4,247' },
      },
      // X2M Gateway (center)
      {
        id: 'x2m-gateway',
        type: 'x2mGateway',
        position: { x: CENTER_X - 70, y: 220 },
        data: {},
      },
      // Blocked Hospital LAN (left of gateway)
      {
        id: 'hospital-lan',
        type: 'blockedLAN',
        position: { x: CENTER_X - 350, y: 320 },
        data: {},
      },
      // Printer (bottom)
      {
        id: 'printer-methodist',
        type: 'printer',
        position: { x: CENTER_X - 10, y: 580 },
        data: {
          model: 'HP LaserJet Enterprise M635',
          location: 'Radiology Department',
          hospital: 'Methodist Hospital • Austin, TX',
          toner: 78,
          uptime: 99.9,
        },
      },
    ];

    const newEdges = [
      // Palo Alto Cloud → X2M (security tunnel)
      {
        id: 'palo-to-x2m',
        source: 'palo-cloud',
        target: 'x2m-gateway',
        targetHandle: 'left',
        type: 'smoothstep',
        animated: true,
        style: { 
          stroke: COLORS.paloAlto, 
          strokeWidth: 3,
          strokeDasharray: '5,5',
        },
        label: 'Prisma Access',
        labelStyle: { fill: COLORS.paloAlto, fontSize: 10, fontWeight: 600 },
        labelBgStyle: { fill: '#FFFFFF', fillOpacity: 0.95 },
        labelBgPadding: [6, 4],
        labelBgBorderRadius: 4,
      },
      // HP Cloud → X2M (management)
      {
        id: 'hp-to-x2m',
        source: 'hp-cloud',
        target: 'x2m-gateway',
        targetHandle: 'right',
        type: 'smoothstep',
        animated: true,
        style: { 
          stroke: COLORS.hp, 
          strokeWidth: 3,
        },
        label: 'Fleet Telemetry',
        labelStyle: { fill: COLORS.hp, fontSize: 10, fontWeight: 600 },
        labelBgStyle: { fill: '#FFFFFF', fillOpacity: 0.95 },
        labelBgPadding: [6, 4],
        labelBgBorderRadius: 4,
      },
      // X2M → Printer
      {
        id: 'x2m-to-printer',
        source: 'x2m-gateway',
        target: 'printer-methodist',
        type: 'smoothstep',
        animated: true,
        style: { 
          stroke: COLORS.secure, 
          strokeWidth: 3,
        },
        label: 'Encrypted • HIPAA',
        labelStyle: { fill: COLORS.secure, fontSize: 10, fontWeight: 600 },
        labelBgStyle: { fill: '#FFFFFF', fillOpacity: 0.95 },
        labelBgPadding: [6, 4],
        labelBgBorderRadius: 4,
      },
      // Blocked connection to Hospital LAN
      {
        id: 'blocked-lan',
        source: 'x2m-gateway',
        target: 'hospital-lan',
        sourceHandle: 'left',
        type: 'smoothstep',
        animated: false,
        style: { 
          stroke: COLORS.blocked, 
          strokeWidth: 2,
          strokeDasharray: '8,8',
          opacity: 0.6,
        },
      },
    ];

    return { nodes: newNodes, edges: newEdges };
  }, []);

  useEffect(() => {
    setLoading(true);
    
    setTimeout(() => {
      const { nodes: newNodes, edges: newEdges } = buildTopology();
      setNodes(newNodes);
      setEdges(newEdges);
      setLoading(false);
    }, 800);
  }, [buildTopology, setNodes, setEdges]);

  return (
    <div className="h-screen w-full bg-white relative overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />
      </div>
      
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-white via-white to-transparent pb-6 px-6 pt-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <span className="w-11 h-11 bg-gradient-to-br from-orange-100 to-red-100 rounded-xl flex items-center justify-center border border-orange-200">
              <svg className="w-6 h-6 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
              </svg>
            </span>
            Sovereign Edge Architecture
          </h1>
          <p className="text-slate-500 text-sm mt-1.5">Celerway X2M + Palo Alto NGFW • Network-Isolated Healthcare Connectivity</p>
          <div className="mt-3 flex items-center gap-2">
            <span className="text-[10px] px-2.5 py-1 bg-orange-100 text-orange-700 rounded-full border border-orange-200 font-medium">
              Supports HIPAA Safeguards
            </span>
            <span className="text-[10px] px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full border border-emerald-200 font-medium">
              Zero Trust
            </span>
            <span className="text-[10px] px-2.5 py-1 bg-red-100 text-red-700 rounded-full border border-red-200 font-medium">
              Threat Prevention
            </span>
          </div>
        </div>
      </div>
      
      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/95">
          <div className="text-center">
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 border-4 border-orange-200 rounded-full" />
              <div className="absolute inset-0 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
              <div className="absolute inset-2 border-4 border-red-200 rounded-full" />
              <div className="absolute inset-2 border-4 border-red-500 border-t-transparent rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
            </div>
            <div className="text-orange-600 text-lg font-semibold">Initializing Sovereign Edge</div>
            <div className="text-slate-400 text-sm mt-1">Establishing secure tunnel...</div>
          </div>
        </div>
      )}
      
      {/* React Flow */}
      {!loading && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2, maxZoom: 0.75 }}
          minZoom={0.25}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.55 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={30} size={1} color="rgba(0, 0, 0, 0.05)" />
          <Controls className="!bg-white !border-slate-200 !rounded-xl !shadow-lg" />
        </ReactFlow>
      )}
    </div>
  );
};

export default SovereignEdgeTopologyLight;
